import { ref, reactive, computed, watch, type Ref } from 'vue'
import { usePredicates } from '@/ContextTab/stores/newsSet/usePredicates'
import { useNewsStore } from '@/ContextTab/stores/news'
import { apiClientV1, type ApiError } from '@/ContextTab/services/apiClient'
import type { TNewsPredicate } from '@/ContextTab/stores/newsSet/usePredicates'
import type { TaskPanelNewsSetType } from '@/lib/types'
import type {
  Errors,
  GetNewsInProgressData,
  GetNewsMyData,
  GetNewsWaitingData,
  NewsInProgressListGetOutReadable,
  NewsMyListGetOutReadable,
  NewsWaitingListGetOutReadable,
} from '@/api/generated/v1'


export function useSet({
  setType,
  predicates,
  setParams = ref({}),
}: {
  setType: TaskPanelNewsSetType
  predicates: TNewsPredicate[]
  setParams?: Ref<any>
}) {
  const newsStore = useNewsStore()
  const { checkAgainstPredicates } = usePredicates()

  const set = reactive<number[]>([])

  newsStore.registerNewsSetForGc(set)

  const params = reactive({
    cursor: undefined,
    reverse: false,
    limit: 30,
  } satisfies GetNewsWaitingData['query'] & GetNewsInProgressData['query'] & GetNewsMyData['query'])

  const hasMoreItems = ref(true)
  const error = ref<ApiError[] | null>(null)
  const isFetching = ref(false)

  const newsList = computed(() => set
    .map((id) => newsStore.news[id])
    .filter((newsItem) => !!newsItem), // TODO: try load such news?
  )

  async function loadNext() {
    if (isFetching.value) {
      return
    }

    try {
      isFetching.value = true

      let __data:
        NewsWaitingListGetOutReadable
        | NewsInProgressListGetOutReadable
        | NewsMyListGetOutReadable
        | undefined
      let __errors: Errors | undefined

      if (setType === 'waiting') {
        const response1 = await apiClientV1.NewsService.getNewsWaiting({
          query: { ...params, ...setParams.value },
        })
        __data = response1.data
        __errors = response1.error
      } else if (setType === 'inProgress') {
        const response2 = await apiClientV1.NewsService.getNewsInProgress({
          query: { ...params, ...setParams.value },
        })
        __data = response2.data
        __errors = response2.error
      } else if (setType === 'my') {
        const response3 = await apiClientV1.NewsService.getNewsMy({
          query: { ...params, ...setParams.value },
        })
        __data = response3.data
        __errors = response3.error
      }

      if (__data?.data) {
        params.cursor = __data.next_cursor as any
        const mapped = __data.data
          ?.map((item: any) => item.id)
          .filter((id) => !set.includes(id)) ?? []

        set.push(...mapped)

        __data.data?.forEach((newsItem: any) => {
          newsStore.setNewsItem(newsItem.id, newsItem)
        })
      } else if (__errors?.errors.length) {
        error.value = __errors.errors
      }
    } finally {
      isFetching.value = false
    }
  }

  async function reload() {
    params.cursor = undefined
    set.length = 0
    await loadNext()
  }

  function unshiftSet(id: number) {
    if (!set.includes(id)) {
      set.unshift(id)
    }
  }

  function removeFromSet(id: number) {
    const index = set.indexOf(id)
    if (index !== -1) {
      set.splice(index, 1)
    }
  }

  async function updateSet(newsItem) {
    const checked = await checkAgainstPredicates(predicates, newsItem)
    if (checked) {
      unshiftSet(newsItem.id)
    } else {
      removeFromSet(newsItem.id)
    }
  }

  function clearStore() {
    set.length = 0
    params.cursor = undefined
    params.reverse = false
  }

  watch(params, (newParams) => {
    hasMoreItems.value = newParams.cursor !== null
  })

  return {
    set,
    newsList,
    hasMoreItems,
    isFetching,
    error,

    loadNext,
    reload,
    updateSet,
    removeFromSet,
    clearStore,
  }
}
